import ErrorMessage from "@/v2-ui/ErrorMessage"
import Dialog, { type DialogProps } from "@/v2-ui/dialog"
import Button from "@/v2-backoffice-client/components/button"
import { useTenantCreateMutation } from "@/v2-backoffice-client/tenant/create/tenant.create.mutation"
import TenantCreateForm from "@/v2-backoffice-client/tenant/create/TenantCreateForm"
import type { TenantCreateFormProps } from "@/v2-backoffice-client/tenant/create/TenantCreateForm"
import type { TenantCreateMutation } from "@/v2-backoffice-client/tenant/create/__types__/tenant.create.mutation"

type CreatedTenant = NonNullable<TenantCreateMutation["createTenant"]>

export type TenantCreateDialogProps = DialogProps & {
  defaultValues?: TenantCreateFormProps["defaultValues"]
  onCreated?(tenant: CreatedTenant): void
}

function TenantCreateDialog(props: TenantCreateDialogProps) {
  const { open, onOpenChange, defaultValues, onCreated, ...dialogProps } = props

  const [createTenant, { loading, error }] = useTenantCreateMutation()

  return (
    <Dialog open={open} onOpenChange={onOpenChange} {...dialogProps}>
      <Dialog.Content align="top">
        <Dialog.Header>
          <Dialog.Title>Create New Team</Dialog.Title>
        </Dialog.Header>

        <ErrorMessage className="my-4 text-center" error={error} />

        <TenantCreateForm
          id="tenant-create-form"
          defaultValues={defaultValues}
          onSubmit={async (data, e) => {
            e.preventDefault()
            const result = await createTenant({
              variables: {
                input: data,
              },
            })
            // errorPolicy is "all" app-wide, so a rejected create resolves
            // instead of throwing — closing here would swallow the error
            // banner above.
            if (result.errors?.length) return
            if (result.data?.createTenant) {
              onCreated?.(result.data.createTenant)
            }
            onOpenChange?.(false)
          }}
        />

        <Dialog.Footer className="flex justify-end gap-3 mt-6">
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              onOpenChange?.(false)
            }}
          >
            Cancel
          </Button>

          <Button type="submit" isLoading={loading} form="tenant-create-form">
            {loading ? "Creating..." : "Create"}
          </Button>
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog>
  )
}

export default TenantCreateDialog
